/**
 * Superboba BOGO Widget
 *
 * Handles tier selection, subscribe toggle (with dynamic price swap),
 * flavor dropdown visibility, and add-to-cart with cart drawer integration.
 *
 * Supports both native <select> and custom image dropdowns (data-sb-qb-cs).
 * Supports multiple instances per page (namespaced by section wrapper).
 */

(function () {
  'use strict';

  const init = () => {
    document.querySelectorAll('[data-sb-bogo]').forEach(initSection);
  };

  const initSection = (section) => {
    const dataEl = section.querySelector('[data-sb-qb-products]');
    if (!dataEl) return;

    let products;
    try {
      products = JSON.parse(dataEl.textContent);
    } catch (e) {
      return;
    }

    /* ── Cache DOM ── */
    const tiers = section.querySelectorAll('[data-sb-qb-tier]');
    const flavorsEl = section.querySelector('[data-sb-qb-flavors]');
    const flavorRows = section.querySelectorAll('[data-sb-qb-flavor-row]');
    const subscribeBox = section.querySelector('[data-sb-qb-subscribe]');
    const atcBtn = section.querySelector('[data-sb-qb-atc]');
    const frequencyEl = section.querySelector('[data-sb-qb-frequency]');
    const hasCustomSelect = section.querySelector('[data-sb-qb-cs]') !== null;

    const getSelectedTier = () => section.querySelector('[data-sb-qb-tier].is-selected');
    const isSubscribed = () => (subscribeBox ? subscribeBox.checked : false);

    /* ── Tier Selection ── */
    const selectTier = (tier) => {
      tiers.forEach((t) => {
        t.classList.remove('is-selected');
        const radio = t.querySelector('input[type="radio"]');
        if (radio) radio.checked = false;
      });

      tier.classList.add('is-selected');
      const radio = tier.querySelector('input[type="radio"]');
      if (radio) radio.checked = true;

      updateDropdowns();
      updateFrequency();
    };

    tiers.forEach((tier) => {
      const header = tier.querySelector('[data-sb-qb-tier-header]');
      if (header) header.addEventListener('click', () => selectTier(tier));

      const radio = tier.querySelector('input[type="radio"]');
      if (radio) radio.addEventListener('change', () => selectTier(tier));
    });

    /* ── Subscribe Toggle ── */
    if (subscribeBox) {
      subscribeBox.addEventListener('change', updatePrices);
    }

    /* ── Custom Select Dropdowns ── */
    if (hasCustomSelect) {
      section.querySelectorAll('[data-sb-qb-cs]').forEach((cs) => {
        const trigger = cs.querySelector('[data-sb-qb-cs-trigger]');
        const optionsEl = cs.querySelector('[data-sb-qb-cs-options]');
        if (!trigger || !optionsEl) return;

        trigger.addEventListener('click', (e) => {
          e.stopPropagation();
          const isOpen = trigger.getAttribute('aria-expanded') === 'true';

          /* Close all other open dropdowns in this section */
          section.querySelectorAll('[data-sb-qb-cs-trigger][aria-expanded="true"]').forEach((t) => {
            if (t !== trigger) {
              t.setAttribute('aria-expanded', 'false');
              const opts = t.closest('[data-sb-qb-cs]').querySelector('[data-sb-qb-cs-options]');
              if (opts) opts.hidden = true;
            }
          });

          trigger.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
          optionsEl.hidden = isOpen;
        });

        optionsEl.querySelectorAll('[data-sb-qb-cs-option]').forEach((opt) => {
          opt.addEventListener('click', (e) => {
            e.stopPropagation();

            /* Skip sold-out options */
            if (opt.disabled || opt.classList.contains('is-sold-out')) return;

            /* Update trigger display */
            const title = opt.dataset.title;
            const img = opt.dataset.img;
            const variantId = opt.dataset.variantId;
            const productId = opt.dataset.productId;

            trigger.dataset.variantId = variantId;
            trigger.dataset.productId = productId;

            const labelEl = trigger.querySelector('.sb-qb__cs-label');
            if (labelEl) labelEl.textContent = title;

            const imgEl = trigger.querySelector('.sb-qb__cs-img');
            if (imgEl && img) {
              imgEl.src = img;
              imgEl.alt = title;
            }

            /* Update selected state */
            optionsEl.querySelectorAll('[data-sb-qb-cs-option]').forEach((o) => o.classList.remove('is-selected'));
            opt.classList.add('is-selected');

            /* Close dropdown */
            trigger.setAttribute('aria-expanded', 'false');
            optionsEl.hidden = true;

            /* Swap gallery to show selected flavor */
            updateGallery(productId);
          });
        });
      });

      /* Close on outside click */
      document.addEventListener('click', () => {
        section.querySelectorAll('[data-sb-qb-cs-trigger][aria-expanded="true"]').forEach((t) => {
          t.setAttribute('aria-expanded', 'false');
          const opts = t.closest('[data-sb-qb-cs]').querySelector('[data-sb-qb-cs-options]');
          if (opts) opts.hidden = true;
        });
      });
    }

    /* ── Flavor Swatch Picker ── */
    const swatches = section.querySelectorAll('[data-sb-qb-swatch]');
    const swatchNameEl = section.querySelector('[data-sb-qb-swatch-name]');

    swatches.forEach((swatch) => {
      swatch.addEventListener('click', () => {
        /* Update active state */
        swatches.forEach((s) => s.classList.remove('is-active'));
        swatch.classList.add('is-active');

        /* Update label */
        if (swatchNameEl) swatchNameEl.textContent = swatch.dataset.title;

        /* Swap gallery */
        updateGallery(swatch.dataset.productId);
      });
    });

    /* ── Update Dropdowns ── */
    function updateDropdowns() {
      const selected = getSelectedTier();
      if (!selected || !flavorsEl) return;

      const qty = parseInt(selected.dataset.sbQbQty) || 1;
      const target = selected.querySelector('[data-sb-qb-dropdown-target]');

      if (target) {
        target.appendChild(flavorsEl);
        flavorsEl.style.display = '';
      }

      flavorRows.forEach((row, i) => {
        row.style.display = i < qty ? '' : 'none';
      });
    }

    /* ── Update Prices ── */
    function updatePrices() {
      const mode = isSubscribed() ? 'sub' : 'otp';

      section.querySelectorAll('[data-sb-qb-price]').forEach((el) => {
        const val = el.getAttribute('data-' + mode + '-price') || '';
        el.textContent = val;
      });

      section.querySelectorAll('[data-sb-qb-compare]').forEach((el) => {
        const val = el.getAttribute('data-' + mode + '-compare') || '';
        el.textContent = val;
        el.style.display = val ? '' : 'none';
      });

      section.querySelectorAll('[data-sb-qb-savings]').forEach((el) => {
        const val = el.getAttribute('data-' + mode + '-savings') || '';
        el.textContent = val;
        el.style.display = val ? '' : 'none';
      });
    }

    /* ── Update Gallery ── */
    const pageProductId = section.dataset.sbPageProductId || '';

    function updateGallery(productId) {
      const mediaWrapper = document.querySelector('.product__media-wrapper');
      if (!mediaWrapper) return;

      const pid = String(productId);
      const allSlides = mediaWrapper.querySelectorAll('.product__media-list .product__media-item');
      const allThumbs = mediaWrapper.querySelectorAll('.thumbnail-list .thumbnail-list__item');

      /* Show/hide main slides */
      allSlides.forEach((slide) => {
        const slidePid = slide.dataset.productId;
        if (String(slidePid) === pid) {
          slide.classList.remove('hidden');
        } else {
          slide.classList.add('hidden');
          slide.classList.remove('is-active');
        }
      });

      /* Activate first visible slide */
      const firstVisible = mediaWrapper.querySelector('.product__media-list .product__media-item:not(.hidden)');
      if (firstVisible) {
        firstVisible.classList.add('is-active');
        /* Scroll slider to first slide */
        const slider = mediaWrapper.querySelector('.product__media-list');
        if (slider) slider.scrollTo({ left: 0, behavior: 'instant' });
      }

      /* Show/hide thumbnails */
      allThumbs.forEach((thumb) => {
        const thumbPid = thumb.dataset.productId;
        if (String(thumbPid) === pid) {
          thumb.classList.remove('hidden');
        } else {
          thumb.classList.add('hidden');
          thumb.querySelector('button')?.removeAttribute('aria-current');
        }
      });

      /* Activate first visible thumbnail */
      const firstVisibleThumb = mediaWrapper.querySelector('.thumbnail-list .thumbnail-list__item:not(.hidden) button');
      if (firstVisibleThumb) {
        firstVisibleThumb.setAttribute('aria-current', 'true');
        /* Scroll thumbnail slider to start */
        const thumbSlider = mediaWrapper.querySelector('.thumbnail-list');
        if (thumbSlider) thumbSlider.scrollTo({ left: 0, behavior: 'instant' });
      }
    }

    /* ── Update Frequency Text ── */
    function updateFrequency() {
      if (!frequencyEl) return;
      const selected = getSelectedTier();
      if (!selected) return;
      const text = (selected.dataset.frequency || '').trim();
      if (text) frequencyEl.textContent = text;
    }

    /* ── Collect items from dropdowns (supports both native + custom) ── */
    function collectItems(qty) {
      const raw = [];

      /* Get selling plan from the selected tier (set per-tier in theme editor) */
      const selected = getSelectedTier();
      let tierSellingPlanId = 0;
      if (isSubscribed() && selected) {
        tierSellingPlanId = parseInt(selected.dataset.sellingPlanId) || 0;
      }

      for (let i = 0; i < qty; i++) {
        const row = flavorRows[i];
        if (!row) continue;

        let variantId;

        /* Custom select */
        const csTrigger = row.querySelector('[data-sb-qb-cs-trigger]');
        if (csTrigger) {
          variantId = parseInt(csTrigger.dataset.variantId);
        } else {
          /* Native select fallback */
          const select = row.querySelector('select');
          if (!select) continue;
          variantId = parseInt(select.value);
        }

        raw.push({ id: variantId, quantity: 1, sellingPlanId: tierSellingPlanId });
      }
      return raw;
    }

    /* ── Add to Cart ── */
    if (atcBtn) {
      atcBtn.addEventListener('click', async () => {
        if (atcBtn.disabled) return;

        const selected = getSelectedTier();
        if (!selected) return;

        const qty = parseInt(selected.dataset.sbQbQty) || 1;
        const textEl = atcBtn.querySelector('[data-sb-qb-atc-text]');
        const origText = textEl ? textEl.textContent : '';

        atcBtn.disabled = true;
        atcBtn.classList.add('is-loading');
        if (textEl) textEl.textContent = 'Adding...';

        const raw = collectItems(qty);

        /* Consolidate duplicates (same variant + same selling plan) */
        const map = new Map();
        raw.forEach((item) => {
          const key = item.id + '_' + item.sellingPlanId;
          if (map.has(key)) {
            map.get(key).quantity += item.quantity;
          } else {
            map.set(key, { ...item });
          }
        });

        const items = [...map.values()].map((item) => {
          const clean = { id: item.id, quantity: item.quantity };
          if (item.sellingPlanId > 0) clean.selling_plan = item.sellingPlanId;
          return clean;
        });

        if (items.length === 0) {
          if (textEl) textEl.textContent = origText;
          atcBtn.disabled = false;
          atcBtn.classList.remove('is-loading');
          return;
        }

        try {
          const res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items }),
          });

          if (!res.ok) throw new Error(res.status);

          /* Apply discount code if set for this tier (sub vs otp) */
          const discountCode = isSubscribed()
            ? (selected.dataset.subDiscountCode || '').trim()
            : (selected.dataset.otpDiscountCode || '').trim();
          if (discountCode) {
            await fetch('/discount/' + encodeURIComponent(discountCode), { method: 'GET' });
          }

          if (textEl) textEl.textContent = 'Added!';

          const drawer = document.querySelector('cart-drawer');
          if (drawer && typeof drawer.fetchAndOpen === 'function') {
            drawer.fetchAndOpen();
          }

          setTimeout(() => {
            if (textEl) textEl.textContent = origText;
            atcBtn.disabled = false;
            atcBtn.classList.remove('is-loading');
          }, 2000);
        } catch (err) {
          if (textEl) textEl.textContent = 'Error - try again';
          setTimeout(() => {
            if (textEl) textEl.textContent = origText;
            atcBtn.disabled = false;
            atcBtn.classList.remove('is-loading');
          }, 3000);
        }
      });
    }

    /* ── Initialize ── */
    updateDropdowns();
    updatePrices();
    updateFrequency();
  };

  /* ── Boot ── */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* ── Shopify theme editor support ── */
  if (window.Shopify && Shopify.designMode) {
    document.addEventListener('shopify:section:load', (event) => {
      const section = event.target.querySelector('[data-sb-bogo]');
      if (section) initSection(section);
    });
  }
})();
